import { Button, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { RangeValue } from "rc-picker/es/interface";
import React, {
  FunctionComponent,
  ReactText,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCompanies,
  selectCompanies,
} from "../../features/company/companySlice";
import {
  fetchContentCategories,
  selectAreContentCategoriesFetching,
  selectContentCategoriesDict,
} from "../../features/contentCategory/contentCategorySlice";
import {
  fetchContents,
  IContent,
  pusblishContentStatus,
  selectContents,
  selectContentsDict,
  selectFilterCompanyIdForContents,
  selectFilterTextForContents,
  selectIsContentFetching,
  setFilterCompanyIdForContents,
  setFilterTextForContents,
  unpublishContentStatus,
} from "../../features/knowledgeBase/contentSlice";
import { PAGE_SIZE } from "../../utils/constants";
import { getDateFilterAndSortProps } from "../common/DateFilter";
import tableStyles from "../common/table.module.scss";
import { TableTools } from "../common/TableTools";
import { CompanyFilter } from "../Filters/Company";
import { useDebounceSelector } from "../utils/useDebounce";
import { Status } from "./Status";
import { getTranslations } from "../../features/translations/translationsUtils";

interface ContentListProps {
  editContent: Function;
}
export const ContentList: FunctionComponent<ContentListProps> = ({
  editContent,
}) => {
  const [selectedRowKeys, selectRowKeys] = useState<ReactText[]>([]);
  const filterValue = useSelector(selectFilterTextForContents);
  const filteredInfo = useSelector(selectFilterCompanyIdForContents);
  const debounceFilterValue = useDebounceSelector(selectFilterTextForContents, 500);
  const [dateFilterValues, setDateFilterValues] = useState<
    RangeValue<moment.Moment>
  >();
  const isFetching = useSelector(selectIsContentFetching);
  const isFetchingCategories = useSelector(selectAreContentCategoriesFetching);
  const categories = useSelector(selectContentCategoriesDict);
  const companies = useSelector(selectCompanies);
  const contentsIdsList = useSelector(selectContents);
  const contentDict = useSelector(selectContentsDict);
  const [showActivationBtns, setShowActivationBtns] = useState(false);
  const dispatch = useDispatch();
  const contentsList = contentsIdsList.map((contentId) => ({
    ...contentDict[contentId],
    categoryName: categories[contentDict[contentId].category as string]?.name,
    companyName: companies[contentDict[contentId].companyId as string]?.name,
  }));
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: ReactText[]) => {
      selectRowKeys(selectedRowKeys);
    },
  };

  useEffect(() => {
    if (selectedRowKeys.length > 0) {
      setShowActivationBtns(true);
    } else {
      setShowActivationBtns(false);
    }
  }, [selectedRowKeys]);

  useEffect(() => {
    dispatch(fetchContents());
    dispatch(fetchCompanies());
    dispatch(fetchContentCategories());
  }, [dispatch]);

  const enableStatus = () => {
    const data = {
      ids: selectedRowKeys.map((key) => key.toString()),
    };
    dispatch(pusblishContentStatus(data));
  };

  const disableStatus = () => {
    const data = {
      ids: selectedRowKeys.map((key) => key.toString()),
    };
    dispatch(unpublishContentStatus(data));
  };

  const columns: ColumnsType<IContent> = [
    {
      title: getTranslations('LIST_TITLE'),
      dataIndex: "title",
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "ascend",
      ellipsis: true,
      sorter: (a: IContent, b: IContent) => a.title.localeCompare(b.title),
      filteredValue: debounceFilterValue,
      onFilter: (value, record) => {
        return (
          record.title.toLowerCase().includes(value.toString().toLowerCase()) ||
          record.categoryName
            ?.toLowerCase()
            .includes(value.toString().toLowerCase()) ||
          record._id?.toLowerCase().includes(value.toString().toLowerCase())
        );
      },
    },
    {
      title: getTranslations('LIST_CATEGORY'),
      dataIndex: "categoryName",
      sortDirections: ["descend", "ascend"],
      ellipsis: true,
      sorter: (a: IContent, b: IContent) =>
        (a.categoryName || "").localeCompare(b.categoryName || ""),
    },
    {
      title: getTranslations('LIST_DATE'),
      dataIndex: "createdAt",
      ...getDateFilterAndSortProps<IContent>(
        dateFilterValues,
        setDateFilterValues
      ),
      render: (createdAd) => {
        return moment(createdAd).format("DD-MM-YYYY");
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: getTranslations('LIST_STATUS'),
      dataIndex: "published",
      render: (published) => {
        return <Status status={published} />;
      },
      sortDirections: ["descend", "ascend"],
      sorter: (a: IContent, b: IContent) =>
        a.published === b.published ? 0 : a.published > b.published ? 1 : -1,
    },
    {
      title: getTranslations('LIST_COMPANY'),
      dataIndex: "companyName",
      sortDirections: ["descend", "ascend"],
      filteredValue: filteredInfo ? [filteredInfo] : undefined,
      onFilter: (value, record: IContent) => {
        return record.companyId === value;
      },
      ellipsis: true,
      sorter: (a: IContent, b: IContent) =>
        (a.companyName || "").localeCompare(b.companyName || ""),
      render: (companyName, content) => {
        return (
          <React.Fragment>
            {companyName}
            <div className={tableStyles.actions}>
              <Button
                type="default"
                onClick={() => {
                  editContent(content._id);
                }}
              >
                {getTranslations('LIST_EDIT')}
              </Button>
            </div>
          </React.Fragment>
        );
      },
    },
  ];
  const setFilterValue = (value: string[]) => {
    dispatch(setFilterTextForContents(value[0] || ""));
  }

  const setFilteredInfo = (value: string) => {
    dispatch(setFilterCompanyIdForContents(value || ""));
  };
  return (
    <React.Fragment>
      <TableTools
        placeholder={getTranslations('LIST_SEARCH_IN_TEURONA')}
        onSearch={(value) => setFilterValue([value])}
        onChange={(event) => setFilterValue([event.target.value])}
        onEnable={() => enableStatus()}
        onDisable={() => disableStatus()}
        value={filterValue}
        activationBtnsVisible={showActivationBtns}
        filter={<CompanyFilter onApply={setFilteredInfo} value={filteredInfo} />}
        enableLabel={getTranslations('LIST_PUBLISH')}
        disableLabel={getTranslations('LIST_UNPUBLISH')}
      />
      <Table<IContent>
        loading={isFetchingCategories || isFetching}
        className={tableStyles.table}
        rowClassName={() => tableStyles.row}
        rowKey="_id"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={contentsList}
        pagination={{ position: ["bottomRight"], pageSize: PAGE_SIZE, showSizeChanger: false }}
      />
    </React.Fragment>
  );
};
